import videojs from 'video.js';

import { loadPresentationData, overrideServerSettings, ensurePresentationAvailable, applyLoaderCallbacks, applyPlayerSource, applyPlayerOptions } from './loader/';
import { isPresentationId, applyCustomStyles } from '../../util';
import { QueryStringParamProvider, getPresentationId } from '../../implementations/player-page/helpers.js';
import { getPosterInfo, PresentationStatus } from '../../api/presentation';
import { reportMediaOpen } from '../../api/player';

const _options = new WeakMap();
const _currentOptions = new WeakMap();

const _presentationId = new WeakMap();
const _previousPresentationIds = new WeakMap();
const _isLoadingNewPresentation = new WeakMap();

const _playCoverData = new WeakMap();
const _introCallbacks = new WeakMap();
const _presentationData = new WeakMap();

const _playCoverPromise = new WeakMap();
const _presentationPromise = new WeakMap();

const _playCoverCallbacks = new WeakMap();
const _presentationCallbacks = new WeakMap();
let localize;

/**
 * The Loader module.
 * @member {LoaderPlugin} loader
 * @memberof MediasitePlayer#
 */

/**
 * Provides methods to assist with the loading of Mediasite data from the server.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class LoaderPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);

        _playCoverCallbacks.set(this, []);
        _presentationCallbacks.set(this, []);
        _introCallbacks.set(this, []);

        const { presentationId, ...initOptions } = options;
        if (presentationId) {
            this.init(presentationId, initOptions);
        }
    }

    /**
     * The ID of the Mediasite presentation associated with this player
     * @type {string}
     * @example
     * const presentationId = player.loader.presentationId;
     */
    get presentationId() {
        return _presentationId.get(this);
    }

    /**
     * A boolean indicating whether or not the play cover has been loaded yet.
     * @type {boolean}
     * @example
     * const isPlayCoverLoaded = player.loader.playCoverLoaded;
     */
    get playCoverLoaded() {
        return !(!_playCoverData.get(this));
    }

    /**
     * A boolean indicating whether or not the presentation has been loaded yet and the play cover dismissed.
     * @type {boolean}
     * @example
     * const isPresentationLoaded = player.loader.presentationLoaded;
     */
    get presentationLoaded() {
        return !(!_presentationData.get(this));
    }

    /**
    * Sets up the player for the given presentation and begins loading the play cover data.
    * @param {string} presentationId The ID of the presentation to load.
    * @param {Object} [options]
    * @param {number} [options.playFrom] A time in seconds at which to begin playback.
    * @param {Object} [options.overrides]
    * @private
    * @returns {Promise} A Promise that is resolved with the play cover data when the play cover has finished loading.
    */
    init(presentationId, options = {}) {
        let playCoverPromise = _playCoverPromise.get(this);

        if (!playCoverPromise) {
            this.onLoadPlayCover(applyCustomStyles);
            this.onLoadPresentation(applyCustomStyles);
            this.log(`Attempting to load play cover data for presentation with ID: ${presentationId}`);
            this.player.addClass('vjs-waiting');

            _presentationId.set(this, presentationId);
            _options.set(this, options);

            let coverVisible = options.overrides?.playcover?.visible;
            playCoverPromise = getPosterInfo(presentationId, coverVisible).then(async (playCoverData) => {
                //if either we're already live or if we're autoplaying, we want to hide the playcover
                const params = new QueryStringParamProvider(location.search);
                const autoPlayParam = params.getParamFromOptions(['autostart', 'autoplay']);
                if (playCoverData.playStatus == PresentationStatus.Live) {
                    if (autoPlayParam === 'false') {
                        coverVisible = true;
                    } else {
                        coverVisible = false;
                    }
                } else if (autoPlayParam === 'true') {
                    coverVisible = false;
                }

                options.overrides.playcover.visible = coverVisible;
                _options.set(this, options);

                //Sets title in playcover lower left
                this.player.metadata().title = playCoverData.title;
                this.player.playcover().visible = coverVisible;

                if (coverVisible) {
                    // Todo: include this in the playCoverCallbacks so that loading is delayed until the poster image has been loaded over the network
                    const posterPromise = new Promise((resolve, reject) => {
                        if (!playCoverData.poster) {
                            reject();
                        } else {
                            const img = document.createElement('img');
                            img.addEventListener('load', () => {
                                this.player.poster(playCoverData.poster);
                                resolve();
                            });
                            img.src = playCoverData.poster;
                        }
                    }).catch(() => {
                        this.log('There was not a valid playcover poster to display.');
                    });
                }
                const playCoverCallbacks = _playCoverCallbacks.get(this);
                const { playerOptions } = await applyLoaderCallbacks(this.player, presentationId, playCoverData, { playerOptions: _options.get(this)?.overrides ?? {} }, playCoverCallbacks);
                applyPlayerOptions(this.player, playerOptions);

                _playCoverData.set(this, playCoverData);
                this.player.removeClass('vjs-waiting');
                this.player.trigger('playcoverloaded');
                this.log('Finished loading play cover data.');

                if (!coverVisible || !isPresentationId(presentationId)) {
                    this.loadPresentation();
                }

                if (isPresentationId(presentationId) && playCoverData.playStatus === PresentationStatus.NotAvailable) {
                    let message = localize('The presentation is currently unavailable. Please try again later.');

                    if (playCoverData.isArchived === true) {
                        message = localize('This presentation has been archived, and is unavailable. Please contact your administrator');
                    }
                    this.player.playcover().showPlayButton = false;
                    this.player.removeClass('vjs-waiting');
                    this.player.showMessage(message, { uncloseable: true });
                }

                return playCoverData;
            });

            _playCoverPromise.set(this, playCoverPromise);
        }

        return playCoverPromise;
    }


    applyLiveWait(presentationData) {
        const streams = presentationData.streams;
        if (presentationData?.metadata?.playStatus === 'OnDemand' || presentationData?.metadata?.playStatus === 'NotAvailable' || presentationData?.metadata?.playStatus == null || !this.player?.live) {
            return Promise.resolve(presentationData);
        } else {
            this.player.live().segmentLength = streams.find(src => this.player.canPlayType(src?.items[0]?.type))?.items[0]?.segmentLength ?? this.player.live()?.segmentLength;
            return this.player.live().waitUntilSafe().then(() => {
                return Promise.resolve(presentationData);
            });
        }
    }

    /**
    * Begins loading the presentation, requesting the needed data from the server in the process.  Loads intro and outro if enabled.
    * @returns {Promise<PresentationData>} A Promise that is resolved when the presentation has finished loading and the play cover has been dismissed.
    * @example
    * player.loader.loadPresentation().then(() => {
    *     console.log('Presentation has been loaded, and the play cover has been dismissed.');
    * });
    */
    loadPresentation() {
        let presentationPromise = _presentationPromise.get(this);

        if (!presentationPromise) {
            this.log(`Attempting to load presentation data for presentation with ID: ${this.presentationId}`);
            this.player.addClass('vjs-waiting');

            presentationPromise = loadPresentationData(this.presentationId)
                .then(data => overrideServerSettings(data, _options.get(this)?.overrides))
                .then(ensurePresentationAvailable)
                // Success callback from ensurePresentationAvailable loads intro if enabled, failure callback just loads the main presentation to display the error message
                .then(this.loadIntroIfEnabled.bind(this), this.loadMainPresentation.bind(this))
                .then((mainData) => {
                    // Set up the outro if it exists, then add a presentation ended listener to play it
                    if (this.presentationLoaded) {
                        this.loadOutroIfEnabled(mainData);
                    } else {
                        this.player.one('presentationloaded', this.loadOutroIfEnabled.bind(this, mainData));
                    }

                    return mainData;
                })
                .catch((error) => { //this does swallow a lot of code errors that occur when developing, so consider commenting out when deving
                    //let's make sure we display any issues that went wrong to the user so that they don't just sit and watch the spinner forever (MVP#1104)
                    this.player.error(error.message);
                });

            _presentationPromise.set(this, presentationPromise);
        }

        return presentationPromise;
    }


    /**
     * Determines whether to load the intro presentation, then loads it before the main presentation if enabled.
     * @param {PresentationData} mainPresentationData
     */
    loadIntroIfEnabled(mainPresentationData) {
        // Make sure intro is enabled and presentation is not live
        if (this.shouldLoadIntro(mainPresentationData)) {
            // Manually set duration before the loader callbacks, because chapters relies on it and it will otherwise return 0 temporarily
            const setDuration = (pId, pcd, pData) => {
                this.player.duration(pData.metadata.duration / 1000);
            };
            this.onLoadPresentation(setDuration.bind(this), 0);
            return loadPresentationData(mainPresentationData.playerOptions.intro.id)
                .then(ensurePresentationAvailable)
                // Success callback for the ensurePresentationAvailable function.  Play the intro.
                .then((introData) => {
                    this.#resetPlugins({ except: ['quizzes'] });  // Don't reset quizzes because the plugin is initialized on playCover load
                    // Execute callbacks after loading the intro, or if intro can't be loaded.  As of this comment, it should only be pre-roll.
                    //  If the intro presentation isn't available, it should display when the main presentation starts.
                    _introCallbacks.get(this).forEach(callback => callback(this.presentationId, _playCoverData.get(this), mainPresentationData));

                    // applyPlayerSource is what sets the src for the video elements.  It's where the magic happens for this intro/outro stuff.
                    return applyPlayerSource(this.player, introData).then(async (data) => {
                        // Save the ended banner for end of outro or main presentation
                        this.player.banner().endedEnabled = false;
                        this.player.autoplay(true);
                        this.player.intro().isPlaying = true;
                        // Disable the playcover for the remainder of playback
                        this.player.poster('');
                        // Load the main presentation once the intro has ended
                        this.player.one('ended', () => {
                            this.player.getChild('ControlBar').hide();
                            return this.loadMainPresentation(mainPresentationData, true).then(() => this.player.playcover().visible = false);
                        });

                        return mainPresentationData;
                    })
                },
                // Failure callback.  Intro presentation is not available so load the main presentation.
                () => {
                    return this.loadMainPresentation(mainPresentationData, false).then(() => this.player.playcover().visible = false);
                });
        } else {
            return this.loadMainPresentation(mainPresentationData, false);
        }
    }

    /**
     * Determines whether to load the outro presentation, then loads it after the main presentation if enabled.
     * @param {PresentationData} mainPresentationData
     */
    loadOutroIfEnabled(mainPresentationData) {
        // Make sure outro is enabled and presentation is not live.  Also, no quizzes.
        if (this.shouldLoadOutro(mainPresentationData, _playCoverData.get(this))) {
            loadPresentationData(mainPresentationData.playerOptions.outro.id)
                .then(ensurePresentationAvailable)
                .then((outroData) => {
                    // Make sure the ended banner doesn't display at end of main presentation
                    this.player.banner().endedEnabled = false;
                    // Disable the playcover for the remainder of playback
                    this.player.poster('');
                    this.player.one('ended', () => {
                        MediasitePlayer.setUpOverflowMenu(this.player);
                        this.player.playcover().visible = false;
                        this.player.getChild('ControlBar').hide();
                        this.player.one('playing', () => {
                            this.player.getChild('ControlBar').show();
                        });
                        // As with intro, here's the magic function
                        return applyPlayerSource(this.player, outroData).then(async (data) => {
                            this.#resetPlugins({ except: ['quizzes'] });
                            // Finally show the ended banner if it's enabled
                            this.player.banner().endedEnabled = mainPresentationData.playerOptions.banner.endedEnabled;
                            this.player.autoplay(true);
                            this.player.outro().isPlaying = true;
                        });
                    });
                });
        } else {
            // No outro.  We've disabled the banner, so we'll enable it for the presentation if it's supposed to be enabled.
            this.player.banner().endedEnabled = mainPresentationData.playerOptions.banner.endedEnabled;
        }
    }

    /**
     * Loads the main presentation, either after the intro has finished, upon playcover click, or upon initialization.
     * @param {PresentationData} data
     */
    loadMainPresentation(data, introPlayed) {
        if (introPlayed !== true) {
            // If there's no intro, intro callbacks (pre-roll to be specific) become presentation callbacks
            _introCallbacks.get(this).map((cb) => this.onLoadPresentation(cb));
        }

        _currentOptions.set(this, _options.get(this));

        const presentationCallbacks = _presentationCallbacks.get(this);
        return this.applyLiveWait(data)
            .then(data => applyLoaderCallbacks(this.player, this.presentationId, _playCoverData.get(this), data, presentationCallbacks))
            .then(data => applyPlayerSource(this.player, data)).then(async (presentationData) => {
                const duration = this.player.duration();
                this.initBookmarkStorage(duration, presentationData.metadata.serverClockSkew);
                const playFrom = _options.get(this).playFrom ?? (presentationData.metadata.bookmark || this.getStoredBookmarkPosition());

                const playStatus = presentationData.metadata.playStatus;
                const isDVR = presentationData.playerOptions.live?.enableDvr;

                let seekDisabledInQuizSettings = false;
                if (this.player.usingPlugin('quizzes')) {
                    seekDisabledInQuizSettings = !this.player.quizzes().enableSeeking;
                }

                //during live presentation, a user isn't able to seek, unless it is DVR. Thus, we should be at the live edge
                if (playStatus === PresentationStatus.Live || playStatus === PresentationStatus.LivePaused) {
                    //if it is DVR, respect the playfrom value. If it is infinity, that's slang for moving it to the edge of the presentation
                    if (isDVR) {
                        if (playFrom === Infinity) {
                            this.player.getChild('LiveTracker')?.seekToLiveEdge();
                        } else {
                            this.seekToPlayFrom(playFrom);
                        }
                    } else {
                        this.player.getChild('LiveTracker')?.seekToLiveEdge();
                    }
                } else { //Dealing with playfrom when it comes to On Demand content
                    //move the player's current time to the playfrom time as long as quizzing's seeking is not disabled
                    //why just quizzing disabling seeking? This would enable someone to use the playfrom parameter to retake a subquiz
                    //which would be cheating possibly
                    if (!seekDisabledInQuizSettings) {
                        this.seekToPlayFrom(playFrom);
                    }
                }

                //if the duration parameter has been set, pause the video after the amount of time stated in the parameter
                this.initDurationPause(_options.get(this).duration, playFrom, duration);
                MediasitePlayer.setUpOverflowMenu(this.player);

                this.player.bookmarkStorage().lastStartTime = playFrom >= 0 ? playFrom : 0;
                this.player.inactivity().sessionTimeout = presentationData.playerOptions?.inactivity?.sessionTimeout;
                const playbackTicket = this.player.reporting().playbackTicket;
                if (playbackTicket) {
                    reportMediaOpen(playbackTicket);
                }
                this.player.reporting().reportToDataProvider(this.player.reporting().playbackTicket);
                this.player.reporting().trackPresentation();
                //Sets title for info sidebar when playcover doesn't exist
                this.player.metadata().title = presentationData.metadata.title;

                if (presentationData.playerOptions?.seekThumbnails?.enabled && !seekDisabledInQuizSettings) {
                    this.player.seekThumbnails().init(presentationData.playerOptions?.seekThumbnails?.thumbData);
                }

                this.player.removeClass('vjs-waiting');
                _presentationData.set(this, presentationData);
                this.player.trigger('presentationloaded');
                this.log('Finished loading presentation data.');
                if (introPlayed) {
                    this.player.intro().isPlaying = false;
                    this.player.one('playing', () => {
                        this.player.getChild('ControlBar').show();
                    });
                }

                return presentationData;
            });
    }

    /**
     * Reloads and replays the main presentation (called when replay is clicked at the end of the outro)
     */
    replayMainPresentation() {
        this.#resetPlugins();
        _presentationPromise.set(this, null);
        _presentationData.set(this, null);
        
        this.loadPresentation().then(() => {
            this.player.playcover().visible = false;
            this.player.currentTime(0);
            this.player.autoplay(true);
        });
    }

    /**
     * @callback LoaderCallback
     * @param {string} presentationId
     * @param {PresentationData} presentationData
     * @param {PlayCoverData} playCoverData
     */

    /**
     * Registers a callback that will be passed the play cover information once loading has completed, but before the "loading" spinner has been dismissed.
     * @param {LoaderCallback} callback
     */
    onLoadPlayCover(callback) {
        _playCoverCallbacks.get(this).push(callback);
    }

    /**
     * Registers a callback that will be passed the presentation information once loading has completed, but before the play cover has been dismissed or playback begun.
     * @param {LoaderCallback} callback
     * @param {number} index
     */
    onLoadPresentation(callback, index) {
        if (typeof index === 'number') {
            _presentationCallbacks.get(this).splice(index, 0, callback);
        } else {
            _presentationCallbacks.get(this).push(callback);
        }
    }

    /**
     * Registers a callback that will be passed the presentation information once the intro begins playing if intro is enabled, and otherwise when the presentationloaded
     * event is triggered.
     * @param {LoaderCallback} callback
     */
    onLoadIntroOrMainPresentation(callback) {
        _introCallbacks.get(this).push(callback);
    }

    /**
     * Returns whether to load the intro based on the presentation data 
     * event is triggered.
     * @param {PresentationData} data
     * @returns {boolean} true if the intro should be loaded, false if not
     */
    shouldLoadIntro(data) {
        if (this.isEditorPreviewIframe()) {
            return false;
        }

        return data?.playerOptions.intro.enabled && data.metadata.playStatus === 'OnDemand';
    }

    /**
     * Returns whether to load the outro based on the presentation data 
     * event is triggered.
     * @param {PresentationData} mainData
     * @returns {boolean} true if the outro should be loaded, false if not
     */
    shouldLoadOutro(mainData, playCoverData) {
        if (this.isEditorPreviewIframe()) {
            return false;
        }

        return mainData?.playerOptions.outro.enabled && mainData.metadata.playStatus === 'OnDemand' && !playCoverData.hasQuizzes;
    }

    /**
     * Looks at window.parent to see if player iframe is in one of the three listed editors
     * (captions, quizzes, interactions) because we don't want intro/outro played there
     * @returns {boolean} true if iframe is in one of the editors, false otherwise
     */
    isEditorPreviewIframe() {
        if (window.self != window.top) {
            const editorUrls = [
                `presentations/${this.presentationId}/quizzes/edit`,
                `captions/${this.presentationId}`,
                `interactions/${this.presentationId}`
            ];

            try {
                let containerUrl = window.parent?.location?.pathname;
                let isEditorPreview = false;

                editorUrls.forEach(url => {
                    if (containerUrl.indexOf(url) !== -1) {
                        isEditorPreview = true;
                    }
                });

                return isEditorPreview;
            } catch (e) {
                // Can't access the parent window's location.  Return false.
                return false;
            }
        }

        return false;
    }

    /**
     * Initializes the bookmarkStorage plugin by setting presentation Id, duration, and server clock skew.
     * @param {number} duration
     * @param {number} skew
     */
    initBookmarkStorage(duration, skew) {
        if (!this.player.reporting().includeBookmark) {
            return;
        }
        const storage = this.player.bookmarkStorage();
        storage.presentationId = this.presentationId;
        storage.presentationDuration = duration;
        storage.clockSkew = skew;
    }

    /**
     * Gets the position in seconds from any locally stored bookmark.
     * @returns {number} the position, in seconds, to start playback from
     */
    getStoredBookmarkPosition() {
        if (!this.player.reporting().includeBookmark) {
            return 0;
        }
        const storage = this.player.bookmarkStorage();
        return storage.retrieve().position ?? 0;
    }

    /**
     * Creates a timed event after x amount of seconds to pause the presentation
     * @param {any} durationPause
     * @param {any} playFrom
     */
    initDurationPause(durationPause, playFrom, duration) {
        if (!durationPause) {
            return;
        }
        //offset the pausing by where the video is starting to play from
        //either through the PlayFrom parameter or bookmarking
        if (playFrom && playFrom !== Infinity && playFrom + durationPause < duration) {
            durationPause += playFrom;
        }

        this.player.timedEvents().add({
            id: 'duration-pause',
            time: durationPause,
            duration: 1,
            type: 'hidden'
        }, () => this.player.pause());
    }

    /**
     * Seeks the presentation to the specific time, checking that we aren't trying to just end the video
     * @param {any} playFrom
     */
    seekToPlayFrom(playFrom) {
        if (playFrom !== Math.round(this.player.duration())) {
            this.player.currentTime(playFrom);
        }
    }

    /**
     * Transitions the player to a new presentation without leaving the page
     * @param {string} presentationId
     * @param {boolean} savePreviousId
     */
    loadNewPresentation(presentationId, savePreviousId = true) {
        if (!isPresentationId(presentationId) || _isLoadingNewPresentation.get(this)) {
            return;
        }
        const previousOptions = _currentOptions.get(this);
        previousOptions.overrides.playcover.visible = false;

        if (savePreviousId) {
            this.#savePreviousId();
        }

        //we want to stay in the same XR state we were in when we triggered the new presentation, so let's grab the xr state now and ask the
        //video 360 plugin to stick us back in XR if we were before the switch
        let inXR = false;
        const isPreviousVideo360 = _presentationData.get(this).metadata.is360Video;

        if (isPreviousVideo360) {
            inXR = this.player.video360?.().isInXR();
            this.player.xr().hasPlayerLoadedXR = false; //resets this values that gets set to true when the presentation loads its XR info
        }

        _playCoverPromise.set(this, null);
        _presentationPromise.set(this, null);
        _isLoadingNewPresentation.set(this, true);

        //any plugin that adds elements without overriding the previous option needs to be cleared out before loading in the new presentation
        this.#resetPlugins();

        //we don't want future items in the playlist to also loop
        this.player.banner().isLooping = false;

        //configure the player so that it is prepared to handle the new presentation
        this.player.pause();
        this.player.autoplay(true);

        this.player.trigger("loadnewpresentation");

        this.init(presentationId, previousOptions).then(() => {
            if (inXR) {
                this.player.video360?.().enterXROnInit();
            }
        });

        this.player.one('presentationloaded', () => {
            const isNextVideo360 = _presentationData.get(this).metadata.is360Video;
            // deactive any 360 stuff if the next video isn't 360
            if (isPreviousVideo360 && !isNextVideo360) {
                this.player.video360?.().xrDispose();
            }
            _isLoadingNewPresentation.set(this, false);
        });

        this.player.currentTime(0);
    }

    #savePreviousId() {
        const previousPresentationId = _presentationId.get(this);
        let previousPresentationIds = _previousPresentationIds?.get(this);
        if (!previousPresentationIds) {
            previousPresentationIds = [];
        }

        previousPresentationIds.push(previousPresentationId);
        _previousPresentationIds.set(this, previousPresentationIds);
    }

    /**
     * Resets all the below plugins, except any specified in the except option.
     */
    #resetPlugins(options) {
        const except = options?.except || [];
        const bundled = ["audioDescriptions", "background", "captions", "chapters", "seekBarSegments", "hotspots", "timedEvents"];
        bundled.forEach(pluginName => {
            if (except.indexOf(pluginName) < 0) {
                const plugin = this.player[pluginName];
                plugin()?.clear();
            }
        });

        if (videojs.getPlugin("conviva") && except.indexOf("conviva") < 0) {
            this.player.conviva().dispose();
        }

        if (videojs.getPlugin("quizzes") && except.indexOf("quizzes") < 0) {
            this.player.quizzes().dispose();
        }

        if (videojs.getPlugin("multistream") && except.indexOf("multistream") < 0) {
            this.player.multistream().dispose();
        }

        if (videojs.getPlugin("intro") && except.indexOf("intro") < 0) {
            this.player.intro().isPlaying = false;
        }

        if (videojs.getPlugin("outro") && except.indexOf("outro") < 0) {
            this.player.outro().isPlaying = false;
        }   
    }

    /**
     * Loads the previous presentation that was loaded before the current one
     */
    loadPreviousPresentation() {
        const previousPresentationId = _previousPresentationIds?.get(this).pop();
        this.loadNewPresentation(previousPresentationId, false);
    }

    /**
     * Checks to see if the presentation we are currently on is the presentation that was first loaded 
     */
    isCurrentPresentationOriginal() {
        const currentPresentationId = _presentationId.get(this);
        const originalPresentationId = getPresentationId(location.pathname, window.SiteData?.PresentationId);

        return (currentPresentationId === originalPresentationId);
    }
}

export default LoaderPlugin;
